import {
  ChangeDetectorRef,
  Component,
  Inject,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { forkJoin, Observable, Subject } from 'rxjs';
import { SimpleModalConfig } from '@frk/eds-components';
import { SubscriptionProductsService } from '@marketing/subscriptions-manage/services/subscription-products.service';
import kebabCase from 'lodash/kebabCase';
import { Logger } from '@utils/logger';
import { TranslateService } from '@shared/translate/translate.service';
import { SubscriptionsService } from '@marketing/subscriptions-manage/services/subscriptions.service';
import { map, switchMap, takeUntil } from 'rxjs/operators';
import {
  ConfigGroup,
  ContactData,
  SelectorChip,
  SubscriptionPreference,
  SubscriptionPreferenceGroup,
  UserConfigData,
} from '@marketing/subscriptions-manage/subscription-manage.interface';
import { DOCUMENT } from '@angular/common';

/**
 * Logger
 */
const logger = Logger.getLogger('SubscriptionsPreferencesModalComponent');

@Component({
  selector: 'ft-subscriptions-preferences-modal',
  templateUrl: './subscriptions-preferences-modal.component.html',
  styleUrls: ['./subscriptions-preferences-modal.component.scss'],
})
export class SubscriptionsPreferencesModalComponent
  implements OnInit, OnDestroy {
  @Input() apiUrl!: string;
  private unsubscribe$: Subject<void> = new Subject<void>();
  private configGroups: Array<ConfigGroup>;
  private userConfigData: SubscriptionPreferenceGroup;
  public userConfigData$: Observable<UserConfigData>;
  public isVisible$: Observable<boolean>;
  public modalConfig: SimpleModalConfig;
  public configSteps: Array<string>;
  public currentConfigStep: number;
  public selectors: Array<SelectorChip[]>;
  public selectedItems: Array<SelectorChip[]>;
  public isLoading = true;
  public getStarted = true;

  constructor(
    private subscriptionsService: SubscriptionsService,
    private subscriptionProductsService: SubscriptionProductsService,
    private translationService: TranslateService,
    private changeDetector: ChangeDetectorRef,
    @Inject(DOCUMENT) readonly documentRef: Document
  ) {
    this.modalConfig = {
      modalId: 'subscriptionsPreferencesModal',
      preTitle: this.translationService.instant(
        'subscriptions.product-preferences-title'
      ),
    };
    this.currentConfigStep = 0;
  }

  ngOnInit(): void {
    this.isVisible$ = this.subscriptionProductsService.getIsPreferencesModalVisible$();
    this.userConfigData$ = this.subscriptionsService.getContactId$().pipe(
      takeUntil(this.unsubscribe$),
      switchMap((contactData: ContactData) => {
        const searchFilterOptions$ = this.subscriptionsService.getApiGetRequest$(
          this.apiUrl + 'searchFilterOptions?region=' + contactData.region
        );
        const subscriptionPreferences$ = this.subscriptionsService.getApiGetRequest$(
          this.apiUrl + 'subscriptionPreference/' + contactData.contactId
        );
        return forkJoin([searchFilterOptions$, subscriptionPreferences$]).pipe(
          map(([searchFilterOptions, subscriptionPreferences]) => {
            return { searchFilterOptions, subscriptionPreferences };
          })
        );
      })
    );

    this.isVisible$
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((isVisible: boolean) => {
        if (isVisible) {
          this.userConfigData$
            .pipe(takeUntil(this.unsubscribe$))
            .subscribe((userConfigData: UserConfigData) => {
              this.configGroups =
                userConfigData.searchFilterOptions.searchFilterOptions;
              this.configSteps = this.configGroups.map((group: ConfigGroup) => {
                return this.translationService.instant(
                  `subscriptions.${group.groupLabel}`
                );
              });
              this.configSteps.push(
                this.translationService.instant(`subscriptions.pref-completed`)
              );
              logger.debug(this.configSteps);

              this.userConfigData = userConfigData.subscriptionPreferences;

              this.selectors = this.getSelectors();
              this.selectedItems = this.getSelectors(true);

              this.isLoading = false;
              this.changeDetector.detectChanges();
            });
        }
      });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.unsubscribe();
  }

  /**
   * Close Configuration modal
   */
  public onModalClose(): void {
    this.subscriptionProductsService.setIsPreferencesModalVisible$(false);
    this.getStarted = true;
    this.enableScrolling();
  }

  /**
   * Update Configuration
   */
  public updatePreferences(): void {
    logger.debug('Update preferences');
    let userSelectedPreferences: SubscriptionPreference[] = [];
    this.selectors.forEach((selector: SelectorChip[], index: number) => {
      const selectedData: SubscriptionPreference[] = selector
        .map((chip: SelectorChip) => {
          const subscriptionPreferenceId = chip?.subscriptionPreferenceId;
          const preference: SubscriptionPreference = {
            dataElement: this.getIntGroupId(this.configGroups[index].groupId),
            dataElementLabel: this.configSteps[index],
            dataValue: chip.chips[0].value,
            active: chip.selected,
          };
          if (subscriptionPreferenceId) {
            preference.subscriptionPreferenceId = subscriptionPreferenceId;
          }
          return preference;
        })
        .filter((data: SubscriptionPreference) => {
          return !!data.active || data?.subscriptionPreferenceId;
        });
      if (selectedData.length > 0) {
        userSelectedPreferences = userSelectedPreferences.concat(selectedData);
      }
    });
    logger.debug(userSelectedPreferences, this.userConfigData);
    // Execute PUT API request
    this.savePreferences(userSelectedPreferences);

    this.subscriptionProductsService.setIsPreferencesModalVisible$(false);
    this.enableScrolling();
  }

  /**
   * Set selected step
   * @param event - number
   */
  public stepSelected(event: number): void {
    logger.debug('Step selected:', event);
    this.currentConfigStep = event;
  }

  /**
   * Select/deselect 'chip'
   * @param $event - Event
   * @param chips - SelectorChip Array from current Config Step
   */
  public changeSelection($event: Event, chips: SelectorChip[]): void {
    logger.debug('Change selection of:', chips);
    const chipClicked = chips.find((chip: SelectorChip) => {
      return chip.id === ($event.currentTarget as HTMLElement).id;
    });
    const chipIndex = chips.indexOf(chipClicked);
    if (this.currentConfigStep < this.selectors.length) {
      this.updateSummaryList(chips[chipIndex]);
      chips[chipIndex].selected = this.selectors[this.currentConfigStep][
        chipIndex
      ].selected = !chips[chipIndex].selected;
    }
  }

  /**
   * Configuration Step back
   */
  public previousStep(): void {
    this.currentConfigStep--;
  }

  /**
   * Configuration Step forward
   */
  public nextStep() {
    this.currentConfigStep++;
  }

  /**
   * Get Subscription Preferences Selectors.
   */
  private getSelectors(onlySelected: boolean = false): Array<SelectorChip[]> {
    return this.configGroups.map((group: ConfigGroup) => {
      const selector: Array<SelectorChip> = [];
      group.values.forEach((chipValue: string) => {
        const subscriptionPreference: SubscriptionPreference = this.userConfigData.subscriptionPreferences.find(
          (preference: SubscriptionPreference) => {
            return (
              this.getIntGroupId(group.groupId) === preference.dataElement &&
              chipValue === preference.dataValue
            );
          }
        );
        if (!onlySelected || !!subscriptionPreference?.active) {
          selector.push({
            id: kebabCase(chipValue),
            subscriptionPreferenceId:
              subscriptionPreference?.subscriptionPreferenceId,
            selected: !!subscriptionPreference?.active,
            chips: [{ label: '', value: chipValue }],
          });
        }
      });
      return selector;
    });
  }

  /**
   * Save preferences through API
   * @param userSelectedPreferences - SubscriptionPreference Array
   */
  private savePreferences(
    userSelectedPreferences: SubscriptionPreference[]
  ): void {
    this.subscriptionsService
      .getApiPutRequest$(
        this.apiUrl + 'subscriptionPreference/' + this.userConfigData.contactId,
        userSelectedPreferences
      )
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(() => this.userConfigData$)
      )
      .subscribe(
        (userConfigData: UserConfigData) => {
          this.userConfigData = userConfigData.subscriptionPreferences;
          logger.debug('Preferences saved');
        },
        (error) => {
          logger.debug('Preferences save error', error);
        }
      );
  }

  /**
   * Update list of selected options for summary step.
   * @param chipClicked - SelectorChip
   */
  private updateSummaryList(chipClicked: SelectorChip): void {
    const test = this.selectedItems[this.currentConfigStep].findIndex(
      (chip: SelectorChip) => chip.id === chipClicked.id
    );
    if (test >= 0) {
      this.selectedItems[this.currentConfigStep].splice(test, 1);
    } else {
      this.selectedItems[this.currentConfigStep].push(chipClicked);
    }
    this.selectedItems[this.currentConfigStep].sort(
      (a: SelectorChip, b: SelectorChip) => {
        return a.id.localeCompare(b.id);
      }
    );
  }

  /**
   * Enable scrolling after modal close
   */
  private enableScrolling(): void {
    const body = this.documentRef.getElementsByTagName(
      'body'
    ) as HTMLCollectionOf<HTMLElement>;
    body[0].style.overflow = 'auto';
  }

  /**
   * Parse string to number
   * @param groupId - Number or String
   */
  private getIntGroupId(groupId: number | string): number {
    if (typeof groupId !== 'number') {
      return parseInt(groupId, 10);
    }
    return groupId;
  }

  public startPreferences() {
    this.getStarted = false;
  }
}
